import { FloatingLayer } from "./FloatingLayer";
import React, { type CSSProperties, type ReactNode } from "react";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { CalendarOutlined, ClockCircleOutlined, CloseCircleFilled, DoubleLeftOutlined, DoubleRightOutlined, LeftOutlined, RightOutlined, SwapRightOutlined } from "@ant-design/icons";
import { Button } from "./Button";
import { Select } from "./Select";
import { useBreakpoint } from "./Grid";
import { cx, useKitDisabled, useKitFieldStatus, useKitSize } from "./fieldContext";
import { useUiLanguage, useUiText } from "./uiText";
import { LANGUAGE_EN } from "../i18n/languageCodes";
import { KEY_ARROW_DOWN, KEY_ENTER, KEY_ESCAPE, KEY_TAB } from "./keyboardKeys";

const REFERENCE_YEAR = 2024;
/** 7. Januar 2024 ist ein Sonntag; Basis für lokalisierte Wochentagsnamen. */
const REFERENCE_SUNDAY_DATE = 7;
const MONTHS_PER_YEAR = 12;
const MONTHS_PER_QUARTER = 3;
const QUARTER_INDEXES = [0, 1, 2, 3];
const YEARS_PER_DECADE = 10;
const DECADE_LAST_OFFSET = 9;
const YEAR_PANEL_CELLS = 12;
const DAYS_PER_WEEK = 7;
const WEEK_ROWS = 6;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const CELL_DIGITS = 2;
const SIZE_SMALL = "small";
const SIZE_LARGE = "large";

/*
 * Datums- und Zeitauswahl des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6; Ersatz für Antd `DatePicker`,
 * `DatePicker.RangePicker` und `TimePicker`). Datumslogik bleibt `dayjs`. Getippter Text wird erst beim Verlassen
 * oder mit Enter übernommen (alle angegebenen Formate), damit Zwischenstände wie „27.11.19“ nicht vorzeitig als
 * 2019 gelten. Arten: Tag, Monat, Quartal, Jahr; `showTime` mit Stunden-/Minutenspalten.
 */

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(quarterOfYear);

export type PickerMode = "date" | "month" | "quarter" | "year" | "week";
type FormatFn = (value: Dayjs) => string;
export type PickerFormat = string | FormatFn | Array<string | FormatFn>;
type DisabledDate = (date: Dayjs, info: { type: PickerMode; from?: Dayjs }) => boolean;
type DisabledTime = (date: Dayjs | null) => { disabledHours?: () => number[]; disabledMinutes?: (hour: number) => number[]; disabledSeconds?: (hour: number, minute: number) => number[] };

export type PickerRef = { nativeElement: HTMLDivElement | null; focus: () => void; blur: () => void };

const DEFAULT_FORMAT: Record<PickerMode, string> = { date: "YYYY-MM-DD", month: "YYYY-MM", quarter: "YYYY-[Q]Q", year: "YYYY", week: "YYYY-wo" };
const EDGE = 8;
const POPUP_GAP = 4;

function formatList(format: PickerFormat | undefined, picker: PickerMode, showTime: boolean): Array<string | FormatFn> {
  const list = format === undefined ? [] : Array.isArray(format) ? format : [format];
  if (list.length) return list;
  return [showTime ? "YYYY-MM-DD HH:mm:ss" : DEFAULT_FORMAT[picker]];
}

function formatValue(value: Dayjs | null | undefined, formats: Array<string | FormatFn>): string {
  if (!value || !value.isValid()) return "";
  const first = formats[0];
  return typeof first === "function" ? first(value) : value.format(first);
}

function parseText(text: string, formats: Array<string | FormatFn>, picker: PickerMode): Dayjs | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  for (const format of formats) {
    if (typeof format !== "string") continue;
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) return picker === "quarter" ? parsed.startOf("quarter") : parsed;
  }
  return null;
}

function isOutOfRange(date: Dayjs, unit: "day" | "month" | "quarter" | "year", minDate?: Dayjs, maxDate?: Dayjs) {
  if (minDate && date.endOf(unit as dayjs.OpUnitType).isBefore(minDate)) return true;
  if (maxDate && date.startOf(unit as dayjs.OpUnitType).isAfter(maxDate)) return true;
  return false;
}

function usePickerPopup(open: boolean, setOpen: (open: boolean) => void) {
  const isMobile = useBreakpoint().md === false;
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    // Das größere Touch-Raster muss auch dann im Bildschirm bleiben, wenn ober- und
    // unterhalb des Feldes jeweils weniger Platz als für einen ganzen Kalender ist.
    middleware: [offset(POPUP_GAP), flip({ padding: EDGE }), shift({ padding: EDGE, crossAxis: isMobile })],
  });
  const dismiss = useDismiss(floating.context);
  const interactions = useInteractions([dismiss]);
  return { floating, ...interactions };
}

// ---------------------------------------------------------------- Kalenderfläche

/** Klassen einer Zelle mit denselben Zuständen wie die bisherigen Antd-Zellen (Theme-Regeln greifen weiter). */
function cellClass(inView: boolean, selected: boolean, today: boolean, disabled: boolean, inRange: boolean) {
  return cx("ck-picker-cell", inView && "ck-picker-cell-in-view", selected && "ck-picker-cell-selected", today && "ck-picker-cell-today", disabled && "ck-picker-cell-disabled", inRange && "ck-picker-cell-in-range");
}

type PanelProps = {
  picker: PickerMode;
  viewDate: Dayjs;
  onViewDateChange: (date: Dayjs) => void;
  isSelected: (date: Dayjs, unit: "day" | "month" | "quarter" | "year") => boolean;
  inRange?: (date: Dayjs) => boolean;
  onSelect: (date: Dayjs) => void;
  disabledDate?: DisabledDate;
  minDate?: Dayjs;
  maxDate?: Dayjs;
};

function PickerPanel({ picker, viewDate, onViewDateChange, isSelected, inRange, onSelect, disabledDate, minDate, maxDate }: PanelProps) {
  const lang = useUiLanguage();
  const { text } = useUiText();
  const [mode, setMode] = React.useState<PickerMode>(picker === "week" ? "date" : picker);
  const today = dayjs();
  const firstDay = lang === LANGUAGE_EN ? 0 : 1;
  // display-format: Monats- und Wochentagsnamen der Kalenderoberfläche folgen der UI-Sprache.
  const monthName = (month: number) => new Intl.DateTimeFormat(lang, { month: "short" }).format(new Date(REFERENCE_YEAR, month, 1));
  const disabled = (date: Dayjs, unit: "day" | "month" | "quarter" | "year", type: PickerMode) => isOutOfRange(date, unit, minDate, maxDate) || Boolean(disabledDate?.(date, { type }));

  const pick = (date: Dayjs, unit: "month" | "year") => {
    // Übergeordnete Ebene gewählt: in der eigentlichen Art weiter, sonst übernehmen.
    if (mode !== picker && !(picker === "quarter" && unit === "year")) {
      onViewDateChange(date);
      setMode(unit === "year" && picker !== "year" ? (picker === "date" || picker === "week" ? "month" : picker) : picker === "week" ? "date" : picker);
      return;
    }
    onSelect(date);
  };

  const header = (title: ReactNode, step: "month" | "year" | "decade") => {
    const shiftBy = (amount: number) => onViewDateChange(step === "month" ? viewDate.add(amount, "month") : viewDate.add(amount * (step === "decade" ? YEARS_PER_DECADE : 1), "year"));
    return (
      <div className="ck-picker-header">
        <button type="button" className="ck-picker-header-super-prev-btn" aria-label={text("Previous year")} onClick={() => shiftBy(step === "month" ? -MONTHS_PER_YEAR : -1)}><DoubleLeftOutlined /></button>
        {step === "month" ? <button type="button" className="ck-picker-header-prev-btn" aria-label={text("Previous page")} onClick={() => shiftBy(-1)}><LeftOutlined /></button> : null}
        <div className="ck-picker-header-view">{title}</div>
        {step === "month" ? <button type="button" className="ck-picker-header-next-btn" aria-label={text("Next page")} onClick={() => shiftBy(1)}><RightOutlined /></button> : null}
        <button type="button" className="ck-picker-header-super-next-btn" aria-label={text("Next year")} onClick={() => shiftBy(step === "month" ? MONTHS_PER_YEAR : 1)}><DoubleRightOutlined /></button>
      </div>
    );
  };

  if (mode === "year") {
    const start = Math.floor(viewDate.year() / YEARS_PER_DECADE) * YEARS_PER_DECADE;
    const years = Array.from({ length: YEAR_PANEL_CELLS }, (_, i) => start - 1 + i);
    return (
      <div className="ck-picker-panel ck-picker-year-panel">
        {header(`${start} – ${start + DECADE_LAST_OFFSET}`, "decade")}
        <div className="ck-picker-grid ck-picker-grid-3">
          {years.map((year) => {
            const date = viewDate.year(year).startOf("year");
            const off = disabled(date, "year", "year");
            return (
              <button key={year} type="button" disabled={off} className={cellClass(!(year < start || year > start + DECADE_LAST_OFFSET), isSelected(date, "year"), year === today.year(), off, false)} onClick={() => pick(date, "year")}>
                <span className="ck-picker-cell-inner">{year}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (mode === "month" || mode === "quarter") {
    const quarter = mode === "quarter";
    const cells = quarter ? QUARTER_INDEXES : Array.from({ length: MONTHS_PER_YEAR }, (_, i) => i);
    return (
      <div className={cx("ck-picker-panel", quarter ? "ck-picker-quarter-panel" : "ck-picker-month-panel")}>
        {header(<button type="button" className="ck-picker-year-btn" onClick={() => setMode("year")}>{viewDate.year()}</button>, "year")}
        <div className={cx("ck-picker-grid", quarter ? "ck-picker-grid-4" : "ck-picker-grid-3")}>
          {cells.map((index) => {
            const date = viewDate.month(quarter ? index * MONTHS_PER_QUARTER : index).startOf("month");
            const unit = quarter ? "quarter" : "month";
            const off = disabled(date, unit, mode);
            return (
              <button key={index} type="button" disabled={off} className={cellClass(true, isSelected(date, unit), today.isSame(date, unit), off, Boolean(inRange?.(date)))} onClick={() => pick(date, "month")}>
                <span className="ck-picker-cell-inner">{quarter ? `Q${index + 1}` : monthName(index)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const monthStart = viewDate.startOf("month");
  const gridStart = monthStart.subtract((monthStart.day() - firstDay + DAYS_PER_WEEK) % DAYS_PER_WEEK, "day");
  // display-format: Monats- und Wochentagsnamen der Kalenderoberfläche folgen der UI-Sprache.
  const weekday = new Intl.DateTimeFormat(lang, { weekday: "short" });
  const monthTitle = new Intl.DateTimeFormat(lang, { month: "long" }).format(viewDate.toDate());
  const title = (
    <>
      <button type="button" className="ck-picker-month-btn" onClick={() => setMode("month")}>{monthTitle}</button>
      {" "}
      <button type="button" className="ck-picker-year-btn" onClick={() => setMode("year")}>{viewDate.year()}</button>
    </>
  );
  return (
    <div className="ck-picker-panel ck-picker-date-panel">
      {header(title, "month")}
      <table className="ck-picker-content" role="grid">
        <thead>
          <tr>{Array.from({ length: DAYS_PER_WEEK }, (_, i) => <th key={i} scope="col">{weekday.format(new Date(REFERENCE_YEAR, 0, REFERENCE_SUNDAY_DATE + ((firstDay + i) % DAYS_PER_WEEK)))}</th>)}</tr>
        </thead>
        <tbody>
          {Array.from({ length: WEEK_ROWS }, (_, w) => (
            <tr key={w}>
              {Array.from({ length: DAYS_PER_WEEK }, (_, d) => {
                const date = gridStart.add(w * DAYS_PER_WEEK + d, "day");
                const off = disabled(date, "day", "date");
                return (
                  <td key={d} className="ck-picker-cell-wrap">
                    <button
                      type="button"
                      disabled={off}
                      aria-label={date.format("YYYY-MM-DD")}
                      aria-pressed={isSelected(date, "day")}
                      className={cellClass(date.isSame(viewDate, "month"), isSelected(date, "day"), date.isSame(today, "day"), off, Boolean(inRange?.(date)))}
                      onClick={() => onSelect(date)}
                    >
                      <span className="ck-picker-cell-inner">{date.date()}</span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- Zeitspalten

function TimeColumns({ value, onChange, minuteStep = 1, showSeconds, disabledTime }: { value: Dayjs | null; onChange: (value: Dayjs) => void; minuteStep?: number; showSeconds: boolean; disabledTime?: DisabledTime }) {
  const base = value ?? dayjs().startOf("day");
  const rules = disabledTime?.(value);
  const columns: Array<{ unit: "hour" | "minute" | "second"; items: number[]; disabled: number[] }> = [
    { unit: "hour", items: Array.from({ length: HOURS_PER_DAY }, (_, i) => i), disabled: rules?.disabledHours?.() ?? [] },
    { unit: "minute", items: Array.from({ length: Math.ceil(MINUTES_PER_HOUR / minuteStep) }, (_, i) => i * minuteStep), disabled: rules?.disabledMinutes?.(base.hour()) ?? [] },
    ...(showSeconds ? [{ unit: "second" as const, items: Array.from({ length: SECONDS_PER_MINUTE }, (_, i) => i), disabled: rules?.disabledSeconds?.(base.hour(), base.minute()) ?? [] }] : []),
  ];
  const listRefs = React.useRef<Array<HTMLUListElement | null>>([]);
  React.useLayoutEffect(() => {
    listRefs.current.forEach((list) => {
      const selected = list?.querySelector<HTMLElement>(".is-selected");
      if (list && selected) list.scrollTop = selected.offsetTop - list.offsetTop;
    });
  }, [value]);
  return (
    <div className="ck-picker-time-panel">
      {columns.map((column, index) => (
        <ul key={column.unit} ref={(node) => { listRefs.current[index] = node; }} className="ck-picker-time-column" role="listbox">
          {column.items.map((item) => {
            const selected = value != null && value[column.unit]() === item;
            const off = column.disabled.includes(item);
            return (
              <li key={item} role="option" aria-selected={selected} aria-disabled={off || undefined} className={cx("ck-picker-time-cell", selected && "is-selected", off && "is-disabled")}
                onClick={() => { if (!off) return onChange(base[column.unit](item)); }}>
                {String(item).padStart(CELL_DIGITS, "0")}
              </li>
            );
          })}
        </ul>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Eingabefeld

type FieldBaseProps = {
  disabled?: boolean;
  allowClear?: boolean | { clearIcon?: ReactNode };
  placeholder?: string;
  size?: "small" | "middle" | "large";
  status?: "error" | "warning" | "";
  variant?: "outlined" | "filled" | "borderless" | "underlined";
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  suffixIcon?: ReactNode;
  id?: string;
  autoFocus?: boolean;
  inputReadOnly?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  popupClassName?: string;
  popupStyle?: CSSProperties;
  classNames?: { popup?: string | { root?: string } };
  styles?: { popup?: { root?: CSSProperties } };
  "aria-label"?: string;
  /** Antd-Optionen ohne Wirkung im Kit. */
  locale?: unknown;
  needConfirm?: boolean;
  showNow?: boolean;
  preserveInvalidOnBlur?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>, info?: { range?: "start" | "end" }) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>, info?: { range?: "start" | "end" }) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, preventDefault: () => void) => void;
  onMouseUp?: React.MouseEventHandler<HTMLInputElement>;
};

function popupClass(classNames: FieldBaseProps["classNames"], popupClassName?: string) {
  const popup = classNames?.popup;
  return cx(popupClassName, typeof popup === "string" ? popup : popup?.root);
}

function useOpenState(openProp: boolean | undefined, defaultOpen: boolean | undefined, onOpenChange: ((open: boolean) => void) | undefined, disabled: boolean) {
  const [inner, setInner] = React.useState(Boolean(defaultOpen));
  const open = !disabled && (openProp ?? inner);
  const setOpen = React.useCallback((next: boolean) => {
    if (openProp === undefined) setInner(next);
    onOpenChange?.(next);
  }, [onOpenChange, openProp]);
  return [open, setOpen] as const;
}

function fieldClasses(base: string, disabled: boolean, size: string | undefined, status: string | undefined, variant: string | undefined, open: boolean) {
  return cx("ck-field", "ck-field--affix", "ck-picker", base, size === SIZE_SMALL && "ck-field--sm", size === SIZE_LARGE && "ck-field--lg", variant && `ck-field--${variant}`, status && `is-${status}`, disabled && "is-disabled ck-picker-disabled", open && "ck-picker-focused");
}

// ---------------------------------------------------------------- DatePicker

export interface DatePickerProps extends FieldBaseProps {
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  onChange?: (date: Dayjs | null, dateString: string) => void;
  onOk?: (date: Dayjs | null) => void;
  format?: PickerFormat;
  picker?: PickerMode;
  showTime?: boolean | { format?: string; minuteStep?: number; defaultValue?: Dayjs };
  disabledDate?: DisabledDate;
  disabledTime?: DisabledTime;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  pickerValue?: Dayjs;
  defaultPickerValue?: Dayjs;
  onPickerValueChange?: (date: Dayjs) => void;
  renderExtraFooter?: () => ReactNode;
}

const DatePickerImpl = React.forwardRef<PickerRef, DatePickerProps>(function DatePicker(props, ref) {
  const {
    value: valueProp, defaultValue, onChange, onOk, format, picker = "date", showTime, disabledDate, disabledTime, minDate, maxDate,
    pickerValue, defaultPickerValue, onPickerValueChange, renderExtraFooter, disabled, allowClear = true, placeholder, size, status, variant,
    className, rootClassName, style, suffixIcon, id, autoFocus, inputReadOnly, open: openProp, defaultOpen, onOpenChange, getPopupContainer,
    popupClassName, popupStyle, classNames, styles, preserveInvalidOnBlur, onBlur, onFocus, onKeyDown, onMouseUp, "aria-label": ariaLabel,
  } = props;
  const { text } = useUiText();
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const withTime = Boolean(showTime);
  const timeConfig = typeof showTime === "object" ? showTime : undefined;
  const formats = formatList(format, picker, withTime);
  const [innerValue, setInnerValue] = React.useState<Dayjs | null>(defaultValue ?? null);
  const value = valueProp !== undefined ? valueProp : innerValue;
  const [open, setOpenState] = useOpenState(openProp, defaultOpen, onOpenChange, mergedDisabled);
  const [draft, setDraft] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<Dayjs | null>(null);
  const [innerView, setInnerView] = React.useState<Dayjs>(() => pickerValue ?? defaultPickerValue ?? value ?? dayjs());
  const viewDate = pickerValue ?? innerView;
  const setViewDate = (date: Dayjs) => { if (pickerValue === undefined) setInnerView(date); onPickerValueChange?.(date); };
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => ({ nativeElement: rootRef.current, focus: () => inputRef.current?.focus(), blur: () => inputRef.current?.blur() }), []);

  const setOpen = (next: boolean) => {
    if (next && !open) { setViewDate(value ?? pickerValue ?? dayjs()); setPending(value ?? null); }
    setOpenState(next);
  };
  const { floating, getReferenceProps, getFloatingProps } = usePickerPopup(open, setOpen);

  const emit = (next: Dayjs | null) => {
    const same = next === value || (next && value && next.isSame(value, withTime ? "second" : "day") && formatValue(next, formats) === formatValue(value, formats));
    if (same) return;
    if (valueProp === undefined) setInnerValue(next);
    onChange?.(next, formatValue(next, formats));
  };

  const commitText = (raw: string) => {
    if (!raw.trim()) { emit(null); return true; }
    const parsed = parseText(raw, formats, picker);
    if (!parsed) return false;
    const unit = picker === "date" || picker === "week" ? "day" : picker;
    if (isOutOfRange(parsed, unit, minDate, maxDate) || disabledDate?.(parsed, { type: picker })) return false;
    emit(parsed);
    setViewDate(parsed);
    return true;
  };

  const select = (date: Dayjs) => {
    if (withTime) {
      const base = pending ?? value ?? timeConfig?.defaultValue ?? dayjs().startOf("day");
      setPending(date.hour(base.hour()).minute(base.minute()).second(base.second()));
      return;
    }
    emit(picker === "quarter" ? date.startOf("quarter") : date);
    setDraft(null);
    setOpen(false);
  };

  const isSelected = (date: Dayjs, unit: "day" | "month" | "quarter" | "year") => {
    const current = withTime ? pending ?? value : value;
    return Boolean(current && current.isSame(date, unit));
  };

  const shown = draft ?? formatValue(value, formats);
  const showClear = Boolean(allowClear) && !mergedDisabled && Boolean(value);
  const portalRoot = getPopupContainer && rootRef.current ? getPopupContainer(rootRef.current) : undefined;
  const timeFormat = timeConfig?.format ?? (typeof formats[0] === "string" && /s/.test(formats[0]) ? "HH:mm:ss" : "HH:mm");

  return (
    <>
      <div
        ref={(node) => { rootRef.current = node; floating.refs.setReference(node); }}
        className={fieldClasses("ck-picker-single", mergedDisabled, mergedSize, mergedStatus, variant, open)}
        style={style}
        {...getReferenceProps({ onMouseDown: (event: React.MouseEvent) => { if (event.target !== inputRef.current) event.preventDefault(); inputRef.current?.focus(); if (!mergedDisabled) setOpen(true); } })}
      >
        <input
          ref={inputRef}
          id={id}
          className={cx("ck-field-input", "ck-picker-input", className, rootClassName)}
          autoComplete="off"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={mergedStatus === "error" || undefined}
          placeholder={placeholder}
          disabled={mergedDisabled}
          readOnly={inputReadOnly}
          autoFocus={autoFocus}
          value={shown}
          onChange={(event) => { setDraft(event.target.value); if (!open) setOpen(true); }}
          onFocus={(event) => { onFocus?.(event); }}
          onBlur={(event) => {
            if (floating.elements.floating?.contains(event.relatedTarget as Node | null)) return;
            if (draft !== null) {
              const ok = commitText(draft);
              if (ok || !preserveInvalidOnBlur) setDraft(null);
            }
            onBlur?.(event);
          }}
          onMouseUp={onMouseUp}
          onKeyDown={(event) => {
            let prevented = false;
            onKeyDown?.(event, () => { prevented = true; });
            if (prevented || event.defaultPrevented) return;
            if (event.key === KEY_ENTER) {
              event.preventDefault();
              if (draft !== null) { if (commitText(draft)) { setDraft(null); setOpen(false); } }
              else if (withTime && pending) { emit(pending); setOpen(false); }
              else setOpen(!open);
            } else if (event.key === KEY_ESCAPE && open) {
              event.preventDefault();
              event.stopPropagation();
              setDraft(null);
              setOpen(false);
            } else if (event.key === KEY_TAB) {
              setOpen(false);
            } else if (event.key === KEY_ARROW_DOWN && !open) {
              setOpen(true);
            }
          }}
        />
        {showClear ? (
          <span className="ck-picker-clear" role="button" aria-label={text("Clear")} onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); setDraft(null); emit(null); }}>
            {typeof allowClear === "object" && allowClear.clearIcon ? allowClear.clearIcon : <CloseCircleFilled />}
          </span>
        ) : null}
        <span className={cx("ck-picker-suffix", showClear && "has-clear")} aria-hidden="true">{suffixIcon ?? <CalendarOutlined />}</span>
      </div>
      {open ? (
        <FloatingLayer root={portalRoot}>
          <div
            ref={floating.refs.setFloating}
            className={cx("ck-picker-dropdown", popupClass(classNames, popupClassName))}
            style={{ ...floating.floatingStyles, zIndex: 1050, ...popupStyle, ...styles?.popup?.root }}
            {...getFloatingProps({ onMouseDown: (event: React.MouseEvent) => { if (!(event.target instanceof HTMLInputElement)) event.preventDefault(); } })}
          >
            <div className="ck-picker-panel-container">
              <div className="ck-picker-panels">
                <PickerPanel picker={picker} viewDate={viewDate} onViewDateChange={setViewDate} isSelected={isSelected} onSelect={select} disabledDate={disabledDate} minDate={minDate} maxDate={maxDate} />
                {withTime ? (
                  <TimeColumns value={pending ?? value} onChange={(next) => setPending(next)} minuteStep={timeConfig?.minuteStep} showSeconds={/s/.test(timeFormat)} disabledTime={disabledTime} />
                ) : null}
              </div>
              {renderExtraFooter ? <div className="ck-picker-footer-extra">{renderExtraFooter()}</div> : null}
              <div className="ck-picker-footer">
                {withTime ? (
                  <>
                    <button type="button" className="ck-picker-now-btn" onClick={() => setPending(dayjs())}>{text("Now")}</button>
                    <Button type="primary" size="small" disabled={!pending} onClick={() => { emit(pending); onOk?.(pending); setOpen(false); }}>{text("OK")}</Button>
                  </>
                ) : picker === "date" ? (
                  <button type="button" className="ck-picker-today-btn" disabled={Boolean(disabledDate?.(dayjs(), { type: "date" })) || isOutOfRange(dayjs(), "day", minDate, maxDate)} onClick={() => select(dayjs().startOf("day"))}>{text("Today")}</button>
                ) : null}
              </div>
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
});

// ---------------------------------------------------------------- RangePicker

type RangeValue = [Dayjs | null, Dayjs | null] | null;
type Preset = { label: ReactNode; value: [Dayjs, Dayjs] | (() => [Dayjs, Dayjs]) };

export interface RangePickerProps extends Omit<FieldBaseProps, "placeholder"> {
  value?: RangeValue;
  defaultValue?: RangeValue;
  onChange?: (dates: RangeValue, dateStrings: [string, string]) => void;
  format?: PickerFormat;
  picker?: PickerMode;
  showTime?: boolean;
  placeholder?: [string, string];
  presets?: Preset[];
  allowEmpty?: [boolean, boolean];
  disabledDate?: DisabledDate;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  separator?: ReactNode;
}

const RangePickerImpl = React.forwardRef<PickerRef, RangePickerProps>(function RangePicker(props, ref) {
  const {
    value: valueProp, defaultValue, onChange, format, picker = "date", showTime, placeholder, presets, allowEmpty, disabledDate, minDate, maxDate,
    separator, disabled, allowClear = true, size, status, variant, className, rootClassName, style, suffixIcon, id, open: openProp, defaultOpen,
    onOpenChange, getPopupContainer, popupClassName, popupStyle, classNames, styles, onBlur, onFocus, "aria-label": ariaLabel,
  } = props;
  const { text } = useUiText();
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const formats = formatList(format, picker, Boolean(showTime));
  const [innerValue, setInnerValue] = React.useState<RangeValue>(defaultValue ?? null);
  const value = valueProp !== undefined ? valueProp : innerValue;
  const [open, setOpenState] = useOpenState(openProp, defaultOpen, onOpenChange, mergedDisabled);
  const [active, setActive] = React.useState<0 | 1>(0);
  const [drafts, setDrafts] = React.useState<[string | null, string | null]>([null, null]);
  const [pendingStart, setPendingStart] = React.useState<Dayjs | null>(null);
  const [viewDate, setViewDate] = React.useState<Dayjs>(() => value?.[0] ?? dayjs());
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputs = [React.useRef<HTMLInputElement>(null), React.useRef<HTMLInputElement>(null)];
  React.useImperativeHandle(ref, () => ({ nativeElement: rootRef.current, focus: () => inputs[0].current?.focus(), blur: () => inputs[0].current?.blur() }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const setOpen = (next: boolean) => {
    if (next && !open) { setViewDate(value?.[0] ?? dayjs()); setPendingStart(null); }
    setOpenState(next);
  };
  const { floating, getReferenceProps, getFloatingProps } = usePickerPopup(open, setOpen);
  const unit = picker === "date" || picker === "week" ? "day" : picker;

  const emit = (next: RangeValue) => {
    const normalized: RangeValue = next && (next[0] || next[1]) ? next : null;
    if (valueProp === undefined) setInnerValue(normalized);
    onChange?.(normalized, [formatValue(normalized?.[0], formats), formatValue(normalized?.[1], formats)]);
  };

  const commitText = (index: 0 | 1, raw: string) => {
    const current: [Dayjs | null, Dayjs | null] = [value?.[0] ?? null, value?.[1] ?? null];
    if (!raw.trim()) {
      if (allowEmpty?.[index] || (!current[0] && !current[1])) { current[index] = null; emit(current); }
      return true;
    }
    const parsed = parseText(raw, formats, picker);
    if (!parsed) return false;
    current[index] = parsed;
    if (current[0] && current[1] && current[1].isBefore(current[0])) current.reverse();
    emit(current);
    return true;
  };

  const select = (date: Dayjs) => {
    const picked = picker === "quarter" ? date.startOf("quarter") : date;
    if (!pendingStart) { setPendingStart(picked); setActive(1); return; }
    const [start, end] = picked.isBefore(pendingStart) ? [picked, pendingStart] : [pendingStart, picked];
    emit([start, unit === "day" ? end : end.endOf(unit as dayjs.OpUnitType).startOf("day")]);
    setPendingStart(null);
    setActive(0);
    setOpen(false);
  };

  const isSelected = (date: Dayjs, cellUnit: "day" | "month" | "quarter" | "year") =>
    Boolean((pendingStart && pendingStart.isSame(date, cellUnit)) || (!pendingStart && value?.[0]?.isSame(date, cellUnit)) || (!pendingStart && value?.[1]?.isSame(date, cellUnit)));
  const inRange = (date: Dayjs) => {
    const [start, end] = pendingStart ? [pendingStart, null] : [value?.[0], value?.[1]];
    return Boolean(start && end && date.isAfter(start, unit as dayjs.OpUnitType) && date.isBefore(end, unit as dayjs.OpUnitType));
  };

  const showClear = Boolean(allowClear) && !mergedDisabled && Boolean(value?.[0] || value?.[1]);
  const choosePreset = (index: number) => {
    const preset = presets?.[index];
    if (!preset) return;
    const range = typeof preset.value === "function" ? preset.value() : preset.value;
    emit([range[0], range[1]]);
    setOpen(false);
  };
  const portalRoot = getPopupContainer && rootRef.current ? getPopupContainer(rootRef.current) : undefined;

  const input = (index: 0 | 1) => (
    <input
      ref={inputs[index]}
      id={index === 0 ? id : undefined}
      className={cx("ck-field-input", "ck-picker-input", active === index && open && "is-active")}
      autoComplete="off"
      aria-label={ariaLabel}
      placeholder={placeholder?.[index]}
      disabled={mergedDisabled}
      value={drafts[index] ?? formatValue(value?.[index], formats)}
      onChange={(event) => { const next = [...drafts] as typeof drafts; next[index] = event.target.value; setDrafts(next); if (!open) setOpen(true); }}
      onFocus={(event) => { setActive(index); onFocus?.(event, { range: index === 0 ? "start" : "end" }); }}
      onBlur={(event) => {
        if (floating.elements.floating?.contains(event.relatedTarget as Node | null)) return;
        const draft = drafts[index];
        if (draft !== null) { commitText(index, draft); const next = [...drafts] as typeof drafts; next[index] = null; setDrafts(next); }
        onBlur?.(event, { range: index === 0 ? "start" : "end" });
      }}
      onKeyDown={(event) => {
        if (event.key === KEY_ENTER) {
          event.preventDefault();
          const draft = drafts[index];
          if (draft !== null && commitText(index, draft)) { const next = [...drafts] as typeof drafts; next[index] = null; setDrafts(next); }
          if (index === 0) inputs[1].current?.focus(); else setOpen(false);
        } else if (event.key === KEY_ESCAPE && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
      }}
    />
  );

  return (
    <>
      <div
        ref={(node) => { rootRef.current = node; floating.refs.setReference(node); }}
        className={cx(fieldClasses("ck-picker-range", mergedDisabled, mergedSize, mergedStatus, variant, open), className, rootClassName)}
        style={style}
        {...getReferenceProps({ onMouseDown: (event: React.MouseEvent) => { if (!(event.target instanceof HTMLInputElement)) { event.preventDefault(); inputs[active].current?.focus(); } if (!mergedDisabled) setOpen(true); } })}
      >
        {input(0)}
        <span className="ck-picker-range-separator" aria-hidden="true">{separator ?? <SwapRightOutlined />}</span>
        {input(1)}
        {showClear ? (
          <span className="ck-picker-clear" role="button" aria-label={text("Clear")} onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); emit(null); }}>
            <CloseCircleFilled />
          </span>
        ) : null}
        <span className={cx("ck-picker-suffix", showClear && "has-clear")} aria-hidden="true">{suffixIcon ?? <CalendarOutlined />}</span>
      </div>
      {open ? (
        <FloatingLayer root={portalRoot}>
          <div
            ref={floating.refs.setFloating}
            className={cx("ck-picker-dropdown", "ck-picker-dropdown-range", popupClass(classNames, popupClassName))}
            style={{ ...floating.floatingStyles, zIndex: 1050, ...popupStyle, ...styles?.popup?.root }}
            {...getFloatingProps({ onMouseDown: (event: React.MouseEvent) => event.preventDefault() })}
          >
            <div className="ck-picker-panel-container ck-picker-panel-layout">
              {presets?.length ? <>
                <div className="ck-picker-presets-mobile">
                  <Select<number> showSearch={false} aria-label={text("Choose period")} placeholder={text("Choose period")}
                    options={presets.map((preset, index) => ({ label: preset.label, value: index }))}
                    onChange={choosePreset} getPopupContainer={node => node.parentElement ?? node} style={{ width: "100%" }} />
                </div>
                <ul className="ck-picker-presets">
                  {presets.map((preset, index) => (
                    <li key={index}>
                      <button type="button" onClick={() => choosePreset(index)}>{preset.label}</button>
                    </li>
                  ))}
                </ul>
              </> : null}
              <div className="ck-picker-panels">
                <PickerPanel picker={picker} viewDate={viewDate} onViewDateChange={setViewDate} isSelected={isSelected} inRange={inRange} onSelect={select} disabledDate={disabledDate} minDate={minDate} maxDate={maxDate} />
              </div>
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
});

/** Datumsauswahl (Ersatz für Antd `DatePicker`) samt `DatePicker.RangePicker`. */
export const DatePicker = Object.assign(DatePickerImpl, { RangePicker: RangePickerImpl });
export const RangePicker = RangePickerImpl;

// ---------------------------------------------------------------- TimePicker

export interface TimePickerProps extends FieldBaseProps {
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  onChange?: (time: Dayjs | null, timeString: string) => void;
  onSelect?: (time: Dayjs) => void;
  format?: PickerFormat;
  minuteStep?: number;
  disabledTime?: DisabledTime;
  use12Hours?: boolean;
  /** Shared input normalization; the picker owns draft, validation and commit. */
  parseInput?: (text: string, current: Dayjs | null) => Dayjs | null;
}

/** Zeitauswahl (Ersatz für Antd `TimePicker`): Eingabe mit Stunden- und Minutenspalten. */
export const TimePicker = React.forwardRef<PickerRef, TimePickerProps>(function TimePicker(props, ref) {
  const {
    value: valueProp, defaultValue, onChange, onSelect, format, parseInput, minuteStep = 1, disabledTime, disabled, allowClear = true, placeholder, size, status,
    variant, className, rootClassName, style, suffixIcon, id, autoFocus, inputReadOnly, open: openProp, defaultOpen, onOpenChange, getPopupContainer,
    popupClassName, popupStyle, classNames, styles, preserveInvalidOnBlur, onBlur, onFocus, onKeyDown, onMouseUp, "aria-label": ariaLabel,
  } = props;
  const { text } = useUiText();
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const formats = formatList(format, "date", false).map((item) => (item === DEFAULT_FORMAT.date ? "HH:mm:ss" : item));
  const [innerValue, setInnerValue] = React.useState<Dayjs | null>(defaultValue ?? null);
  const value = valueProp !== undefined ? valueProp : innerValue;
  const [open, setOpen] = useOpenState(openProp, defaultOpen, onOpenChange, mergedDisabled);
  const [draft, setDraft] = React.useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => ({ nativeElement: rootRef.current, focus: () => inputRef.current?.focus(), blur: () => inputRef.current?.blur() }), []);
  const { floating, getReferenceProps, getFloatingProps } = usePickerPopup(open, setOpen);

  const emit = (next: Dayjs | null) => {
    if (next === value || (next && value && next.isSame(value, "second"))) return;
    if (valueProp === undefined) setInnerValue(next);
    onChange?.(next, formatValue(next, formats));
    if (next) onSelect?.(next);
  };
  const commitText = (raw: string) => {
    if (!raw.trim()) { emit(null); return true; }
    const parsed = parseInput ? parseInput(raw, value ?? null) : parseText(raw, formats, "date");
    if (!parsed) return false;
    const base = value ?? dayjs().startOf("day");
    const next = parseInput ? parsed : base.hour(parsed.hour()).minute(parsed.minute()).second(parsed.second());
    emit(next);
    return true;
  };
  const shown = draft ?? formatValue(value, formats);
  const showClear = Boolean(allowClear) && !mergedDisabled && Boolean(value);
  const portalRoot = getPopupContainer && rootRef.current ? getPopupContainer(rootRef.current) : undefined;
  const firstFormat = formats.find((item): item is string => typeof item === "string") ?? "HH:mm";

  return (
    <>
      <div
        ref={(node) => { rootRef.current = node; floating.refs.setReference(node); }}
        className={fieldClasses("ck-picker-time", mergedDisabled, mergedSize, mergedStatus, variant, open)}
        style={style}
        {...getReferenceProps({ onMouseDown: (event: React.MouseEvent) => { if (event.target !== inputRef.current) event.preventDefault(); inputRef.current?.focus(); if (!mergedDisabled) setOpen(true); } })}
      >
        <input
          ref={inputRef}
          id={id}
          className={cx("ck-field-input", "ck-picker-input", className, rootClassName)}
          autoComplete="off"
          aria-label={ariaLabel}
          aria-invalid={mergedStatus === "error" || undefined}
          placeholder={placeholder}
          disabled={mergedDisabled}
          readOnly={inputReadOnly}
          autoFocus={autoFocus}
          value={shown}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={(event) => onFocus?.(event)}
          onBlur={(event) => {
            if (floating.elements.floating?.contains(event.relatedTarget as Node | null)) return;
            if (draft !== null) { const ok = commitText(draft); if (ok || !preserveInvalidOnBlur) setDraft(null); }
            onBlur?.(event);
          }}
          onMouseUp={onMouseUp}
          onKeyDown={(event) => {
            let prevented = false;
            onKeyDown?.(event, () => { prevented = true; });
            if (prevented || event.defaultPrevented) return;
            if (event.key === KEY_ENTER) {
              event.preventDefault();
              if (draft !== null && commitText(draft)) setDraft(null);
              setOpen(false);
            } else if (event.key === KEY_ESCAPE && open) {
              event.preventDefault();
              event.stopPropagation();
              setDraft(null);
              setOpen(false);
            } else if (event.key === KEY_TAB) setOpen(false);
          }}
        />
        {showClear ? (
          <span className="ck-picker-clear" role="button" aria-label={text("Clear")} onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); setDraft(null); emit(null); }}>
            <CloseCircleFilled />
          </span>
        ) : null}
        <span className={cx("ck-picker-suffix", showClear && "has-clear")} aria-hidden="true">{suffixIcon ?? <ClockCircleOutlined />}</span>
      </div>
      {open ? (
        <FloatingLayer root={portalRoot}>
          <div
            ref={floating.refs.setFloating}
            className={cx("ck-picker-dropdown", "ck-picker-dropdown-time", popupClass(classNames, popupClassName))}
            style={{ ...floating.floatingStyles, zIndex: 1050, ...popupStyle, ...styles?.popup?.root }}
            {...getFloatingProps({ onMouseDown: (event: React.MouseEvent) => event.preventDefault() })}
          >
            <div className="ck-picker-panel-container">
              <TimeColumns value={value} onChange={(next) => { setDraft(null); emit(next); }} minuteStep={minuteStep} showSeconds={/s/.test(firstFormat)} disabledTime={disabledTime} />
              <div className="ck-picker-footer">
                <button type="button" className="ck-picker-now-btn" onClick={() => { setDraft(null); emit(dayjs().second(0)); setOpen(false); }}>{text("Now")}</button>
                <Button type="primary" size="small" onClick={() => setOpen(false)}>{text("OK")}</Button>
              </div>
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
});
